import { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Briefcase, Upload, MapPin, Calendar as CalIcon, IndianRupee, FileText, Trash2 } from "lucide-react";

async function toBase64(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Placement() {
  const { user } = useAuth();
  if (user.role !== "student") return <AdminPlacement />;
  return <StudentPlacement />;
}

function StudentPlacement() {
  const { user, refresh } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [apps, setApps] = useState([]);
  const [resumeFile, setResumeFile] = useState({ name: user.resume_name || "", uploading: false });

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([api.get("/jobs"), api.get("/interviews"), api.get("/applications")]);
      setJobs(a.data); setInterviews(b.data); setApps(c.data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to load placement data");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async (job_id) => {
    try {
      await api.post("/jobs/apply", { job_id });
      toast.success("Applied successfully");
      load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const onResume = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setResumeFile({ name: f.name, uploading: true });
    const data = await toBase64(f);
    try {
      await api.post("/resume", { file_name: f.name, file_data: data });
      toast.success("Resume uploaded");
      await refresh();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
    setResumeFile({ name: f.name, uploading: false });
  };

  return (
    <div className="space-y-6" data-testid="placement-student">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Placement Support</div>
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Career Hub</h1>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold">Resume</div>
            <div className="font-heading text-lg font-semibold text-[#0F172A] mt-1">{user.resume_name || "Not uploaded"}</div>
            <p className="text-xs text-[#475569] mt-1">PDF recommended. This will be shared with recruiters.</p>
          </div>
          <Label className="cursor-pointer">
            <input type="file" className="hidden" onChange={onResume} accept=".pdf,.doc,.docx" data-testid="resume-upload" />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium transition-colors">
              <Upload className="w-4 h-4" /> {user.resume_name ? "Replace Resume" : "Upload Resume"}
            </span>
          </Label>
        </div>
      </div>

      <Tabs defaultValue="jobs">
        <TabsList>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Job Openings</TabsTrigger>
          <TabsTrigger value="interviews" data-testid="tab-interviews">Interviews</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">Placement Status</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger">
            {jobs.length === 0 && <div className="text-sm text-[#64748B]">No jobs posted yet.</div>}
            {jobs.map((j) => (
              <JobCard key={j.id} job={j} onApply={() => apply(j.id)} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="interviews" className="mt-6">
          <div className="space-y-3">
            {interviews.length === 0 && <div className="text-sm text-[#64748B] py-12 text-center">No interviews scheduled.</div>}
            {interviews.map((iv) => (
              <div key={iv.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-center gap-5">
                <CalIcon className="w-8 h-8 text-[#0F2E4A]" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="font-heading font-semibold text-[#0F172A]">{iv.company} · {iv.role}</div>
                  <div className="text-xs text-[#64748B] mt-0.5">{iv.date} at {iv.time} · {iv.mode}</div>
                  {iv.notes && <div className="text-xs text-[#475569] mt-1">{iv.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="status" className="mt-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
                <tr><th className="px-4 py-3">Company</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Applied On</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody data-testid="apps-list">
                {apps.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-[#64748B]">No applications yet.</td></tr>}
                {apps.map((a) => (
                  <tr key={a.id} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{a.company}</td>
                    <td className="px-4 py-3 text-[#475569]">{a.role}</td>
                    <td className="px-4 py-3 text-[#64748B]">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-[#0F2E4A]/10 text-[#0F2E4A]">{a.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function JobCard({ job, onApply }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-md bg-[#059669]/10 grid place-items-center">
          <Briefcase className="w-5 h-5 text-[#059669]" strokeWidth={1.75} />
        </div>
        {job.applied && <span className="text-[10px] uppercase font-semibold px-2 py-1 rounded bg-[#059669]/10 text-[#059669]">Applied</span>}
      </div>
      <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{job.role}</h3>
      <div className="text-sm text-[#475569]">{job.company}</div>
      <p className="text-sm text-[#475569] mt-2 line-clamp-2">{job.description}</p>
      <div className="mt-4 space-y-1.5 text-xs">
        <div className="flex items-center gap-2 text-[#475569]"><IndianRupee className="w-3.5 h-3.5" />{job.package}</div>
        <div className="flex items-center gap-2 text-[#475569]"><MapPin className="w-3.5 h-3.5" />{job.location}</div>
        <div className="flex items-center gap-2 text-[#475569]"><CalIcon className="w-3.5 h-3.5" />Apply by {job.apply_by}</div>
      </div>
      {!job.applied && (
        <Button onClick={onApply} data-testid={`apply-${job.id}`} className="mt-4 w-full bg-[#0F2E4A] hover:bg-[#174169] text-white">
          Apply Now
        </Button>
      )}
    </div>
  );
}

function AdminPlacement() {
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [students, setStudents] = useState([]);
  const [openJob, setOpenJob] = useState(false);
  const [openIv, setOpenIv] = useState(false);
  const [job, setJob] = useState({ company: "", role: "", description: "", package: "", location: "", eligibility: "", apply_by: "" });
  const [iv, setIv] = useState({ student_id: "", job_id: "", date: "", time: "", mode: "Online", notes: "" });

  const load = useCallback(async () => {
    try {
      const [a, b, c, d] = await Promise.all([api.get("/jobs"), api.get("/applications"), api.get("/interviews"), api.get("/students")]);
      setJobs(a.data); setApps(b.data); setInterviews(c.data); setStudents(d.data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to load placement data");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createJob = async (e) => {
    e.preventDefault();
    try { await api.post("/jobs", job); toast.success("Job posted"); setOpenJob(false); setJob({ company: "", role: "", description: "", package: "", location: "", eligibility: "", apply_by: "" }); load(); } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const scheduleIv = async (e) => {
    e.preventDefault();
    try { await api.post("/interviews", iv); toast.success("Interview scheduled"); setOpenIv(false); setIv({ student_id: "", job_id: "", date: "", time: "", mode: "Online", notes: "" }); load(); } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const delJob = async (id) => {
    if (!window.confirm("Delete job?")) return;
    try {
      await api.delete(`/jobs/${id}`);
      load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to delete job");
    }
  };

  const downloadResume = async (sid, name) => {
    try {
      const { data } = await api.get(`/resume/${sid}`);
      const a = document.createElement("a");
      a.href = `data:application/octet-stream;base64,${data.file_data}`;
      a.download = data.file_name; a.click();
    } catch { toast.error(`${name} has no resume`); }
  };

  return (
    <div className="space-y-6" data-testid="placement-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Placement Office</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Placement Management</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={openIv} onOpenChange={setOpenIv}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="schedule-interview-button"><CalIcon className="w-4 h-4 mr-1.5" /> Schedule Interview</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Schedule Interview</DialogTitle></DialogHeader>
              <form onSubmit={scheduleIv} className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Student *</Label>
                  <Select value={iv.student_id} onValueChange={(v) => setIv({ ...iv, student_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="iv-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Job *</Label>
                  <Select value={iv.job_id} onValueChange={(v) => setIv({ ...iv, job_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="iv-job"><SelectValue placeholder="Select job" /></SelectTrigger>
                    <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.company} - {j.role}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Date *" type="date" value={iv.date} onChange={(v) => setIv({ ...iv, date: v })} req testid="iv-date" />
                  <F label="Time *" type="time" value={iv.time} onChange={(v) => setIv({ ...iv, time: v })} req testid="iv-time" />
                </div>
                <F label="Mode" value={iv.mode} onChange={(v) => setIv({ ...iv, mode: v })} testid="iv-mode" />
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Notes</Label>
                  <Textarea data-testid="iv-notes" value={iv.notes} onChange={(e) => setIv({ ...iv, notes: e.target.value })} className="mt-1.5" />
                </div>
                <DialogFooter><Button type="submit" data-testid="save-iv" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">Schedule</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={openJob} onOpenChange={setOpenJob}>
            <DialogTrigger asChild>
              <Button data-testid="post-job-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white"><Plus className="w-4 h-4 mr-1.5" /> Post Job</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Post Job Opening</DialogTitle></DialogHeader>
              <form onSubmit={createJob} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Company *" value={job.company} onChange={(v) => setJob({ ...job, company: v })} req testid="job-company" />
                  <F label="Role *" value={job.role} onChange={(v) => setJob({ ...job, role: v })} req testid="job-role" />
                  <F label="Package *" value={job.package} onChange={(v) => setJob({ ...job, package: v })} req testid="job-package" />
                  <F label="Location *" value={job.location} onChange={(v) => setJob({ ...job, location: v })} req testid="job-location" />
                  <F label="Eligibility *" value={job.eligibility} onChange={(v) => setJob({ ...job, eligibility: v })} req testid="job-eligibility" />
                  <F label="Apply By *" type="date" value={job.apply_by} onChange={(v) => setJob({ ...job, apply_by: v })} req testid="job-applyby" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Description *</Label>
                  <Textarea data-testid="job-desc" value={job.description} onChange={(e) => setJob({ ...job, description: e.target.value })} required className="mt-1.5" />
                </div>
                <DialogFooter><Button type="submit" data-testid="save-job" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">Post Job</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="jobs">
        <TabsList>
          <TabsTrigger value="jobs" data-testid="tab-admin-jobs">Jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="apps" data-testid="tab-admin-apps">Applications ({apps.length})</TabsTrigger>
          <TabsTrigger value="ivs" data-testid="tab-admin-ivs">Interviews ({interviews.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="jobs" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {jobs.map((j) => (
              <div key={j.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">{j.company}</div>
                    <h3 className="font-heading font-semibold text-[#0F172A]">{j.role}</h3>
                  </div>
                  <button onClick={() => delJob(j.id)} data-testid={`del-job-${j.id}`} className="p-1 hover:bg-[#FEF2F2] rounded"><Trash2 className="w-4 h-4 text-[#DC2626]" /></button>
                </div>
                <div className="text-xs text-[#475569] space-y-1 mt-3">
                  <div>{j.package} · {j.location}</div>
                  <div>Apply by {j.apply_by}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="apps" className="mt-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
                <tr><th className="px-4 py-3">Student</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Applied</th><th className="px-4 py-3 text-right">Resume</th></tr>
              </thead>
              <tbody>
                {apps.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#64748B]">No applications.</td></tr>}
                {apps.map((a) => (
                  <tr key={a.id} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{a.student_name}</td>
                    <td className="px-4 py-3">{a.company}</td>
                    <td className="px-4 py-3">{a.role}</td>
                    <td className="px-4 py-3 text-[#64748B]">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadResume(a.student_id, a.student_name)} data-testid={`dl-resume-${a.id}`}>
                        <FileText className="w-3.5 h-3.5 mr-1" /> Resume
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="ivs" className="mt-6">
          <div className="space-y-3">
            {interviews.length === 0 && <div className="text-sm text-[#64748B] py-12 text-center">No interviews scheduled.</div>}
            {interviews.map((i) => (
              <div key={i.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center gap-4">
                <CalIcon className="w-6 h-6 text-[#0F2E4A]" />
                <div className="flex-1">
                  <div className="font-medium text-[#0F172A]">{i.student_name} → {i.company} ({i.role})</div>
                  <div className="text-xs text-[#64748B]">{i.date} at {i.time} · {i.mode}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function F({ label, value, onChange, type = "text", req, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#475569]">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={req} className="mt-1.5" />
    </div>
  );
}
